import React, { useEffect, useState } from 'react';
import { Modal, Table, Tag, Tooltip } from 'antd';
import { ScrollText } from 'lucide-react';
import { get_recipe_processes_with_operations } from '@/utils/api';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import { useDispatch } from 'react-redux';
import { openDrawer } from '@/store/reducers/menu';

export default function MBMRDetailsModal({ open, recipeId, title, onClose }) {
  const { isDark } = useTheme();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (open && recipeId) {
      dispatch(openDrawer({ drawerOpen: false }));
      setLoading(true);
      setData(null);
      get_recipe_processes_with_operations({ recipe_id: recipeId }, (res) => {
        setData(res?.data?.data || []);
        setLoading(false);
      });
    }
  }, [open, recipeId]);

  const handleClose = () => {
    setData(null);
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ScrollText size={18} style={{ color: '#1890ff' }} />
          <span>MBMR Details — {title}</span>
        </div>
      }
      footer={null}
      width={1400}
      centered
      bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', padding: '16px', fontSize: 13 }}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#888' }}>Loading...</div>
      ) : data?.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#aaa' }}>No data found.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {data?.map((process, pIdx) => (
            <div
              key={process.process_id}
              style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}
            >
              {/* Process Header */}
              <div
                style={{
                  background: isDark ? defaultColors.palette.gray[700] : '#204448',
                  color: '#fff',
                  padding: '8px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                }}
              >
                <Tooltip title="Process Number">
                  <Tag color="blue" style={{ color: '#fff', background: 'rgba(255,255,255,0.2)', border: 'none', fontWeight: 600 }}>
                    P{pIdx + 1}
                  </Tag>
                </Tooltip>
                <Tooltip title="Process Name">
                  <span style={{ fontWeight: 600, fontSize: 15 }}>{process.process_name}</span>
                </Tooltip>
                <Tooltip title="Process Code">
                  <span style={{ fontSize: 13, opacity: 0.85 }}>({process.process_code})</span>
                </Tooltip>
                {process.area_names?.length > 0 && (
                  <Tooltip title="Area / Section">
                    <Tag style={{ marginLeft: 'auto', background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff' }}>
                      {process.area_names.join(', ')}
                    </Tag>
                  </Tooltip>
                )}
                {process.roles?.map((r) => (
                  <Tooltip key={r} title="Role">
                    <Tag style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', fontSize: 11 }}>{r}</Tag>
                  </Tooltip>
                ))}
              </div>

              {/* Operations */}
              {process.operations?.length === 0 ? (
                <div style={{ padding: '12px 16px', color: '#aaa', fontStyle: 'italic' }}>No operations defined.</div>
              ) : (
                <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {process.operations?.map((op) => (
                    <div
                      key={op.operation_id}
                      style={{ border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}
                    >
                      {/* Operation Header */}
                      <div
                        style={{
                          background: '#f0f7ff',
                          padding: '6px 12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          flexWrap: 'wrap',
                          borderBottom: '1px solid #e5e7eb',
                        }}
                      >
                        <Tooltip title="Operation Number">
                          <Tag color="geekblue" style={{ fontWeight: 600 }}>{op.operation_number}</Tag>
                        </Tooltip>
                        <Tooltip title="Operation Name">
                          <span style={{ fontWeight: 600, fontSize: 14 }}>{op.operation_name}</span>
                        </Tooltip>
                        <Tooltip title="Operation Code">
                          <span style={{ fontSize: 12, color: '#888' }}>({op.operation_code})</span>
                        </Tooltip>
                        {op.operation_type && (
                          <Tooltip title="Operation Type">
                            <Tag color="purple" style={{ marginLeft: 4 }}>{op.operation_type}</Tag>
                          </Tooltip>
                        )}
                        {op.mode && (
                          <Tooltip title="Execution Mode">
                            <Tag color={op.mode === 'manual' ? 'orange' : 'green'} style={{ textTransform: 'capitalize' }}>{op.mode}</Tag>
                          </Tooltip>
                        )}
                        {op.department && (
                          <Tooltip title="Department">
                            <Tag style={{ marginLeft: 'auto', fontSize: 11 }}>{op.department}</Tag>
                          </Tooltip>
                        )}
                      </div>

                      {/* Sub-operation detail tables */}
                      <div style={{ padding: '6px 12px 10px', display: 'flex', flexDirection: 'column', gap: 8 }}>

                        {/* Ingredients */}
                        {op.ingredients?.length > 0 && (
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#389e0d', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                              <Tooltip title="Raw materials and ingredients used in this operation">
                                <Tag color="green" style={{ fontSize: 12, margin: 0 }}>Ingredients ({op.ingredients.length})</Tag>
                              </Tooltip>
                            </div>
                            <Table size="small" bordered pagination={false}
                              dataSource={op.ingredients.map((ing, i) => ({ ...ing, key: i }))}
                              columns={[
                                { title: 'Material', dataIndex: 'ingredient', key: 'ingredient', render: (t, r) => t || r.work_in_progress_material_name || <span style={{ color: '#aaa' }}>—</span> },
                                { title: 'Code', dataIndex: 'ingredient_code', key: 'code', width: 130, ellipsis: true, render: (t) => <span style={{ fontSize: 11 }}>{t || '—'}</span> },
                                { title: 'Qty', dataIndex: 'quantity', key: 'qty', align: 'center', width: 70, render: (t) => t ?? '—' },
                                { title: 'Unit', dataIndex: 'unit', key: 'unit', align: 'center', width: 50, render: (t) => t || '—' },
                                { title: 'Dev%', dataIndex: 'deviation', key: 'dev', align: 'center', width: 55, render: (t) => t ?? '—' },
                                { title: 'Mode', dataIndex: 'execution_mode', key: 'mode', align: 'center', width: 100 },
                                { title: 'Type', dataIndex: 'material_type', key: 'type', align: 'center', width: 60, render: (t) => t ? <Tag style={{ fontSize: 10 }}>{t}</Tag> : '—' },
                                { title: 'From', dataIndex: 'from_machine', key: 'from', ellipsis: true, render: (t) => t || '—' },
                                { title: 'To', dataIndex: 'to_machine', key: 'to', ellipsis: true, render: (t) => t || '—' },
                              ]}
                              scroll={{ x: 700 }}
                            />
                          </div>
                        )}

                        {/* Monitoring */}
                        {op.monitoring?.length > 0 && (
                          <div>
                            <Tooltip title="Parameters monitored during this operation"><Tag color="blue" style={{ fontSize: 12, marginBottom: 4 }}>Monitoring ({op.monitoring.length})</Tag></Tooltip>
                            <Table size="small" bordered pagination={false}
                              dataSource={op.monitoring.map((m, i) => ({ ...m, key: i }))}
                              columns={[
                                { title: 'Parameter', dataIndex: 'parameter', key: 'param' },
                                { title: 'Code', dataIndex: 'parameter_code', key: 'code', width: 110, render: (t) => <span style={{ fontSize: 11 }}>{t || '—'}</span> },
                                { title: 'Min', dataIndex: 'min_value', key: 'min', align: 'center', width: 60, render: (t) => t ?? '—' },
                                { title: 'Max', dataIndex: 'max_value', key: 'max', align: 'center', width: 60, render: (t) => t ?? '—' },
                                { title: 'Unit', dataIndex: 'unit', key: 'unit', align: 'center', width: 50, render: (t) => t || '—' },
                                { title: 'Readings', dataIndex: 'reading_count', key: 'readings', align: 'center', width: 70 },
                                { title: 'Mode', dataIndex: 'mode', key: 'mode', align: 'center', width: 70, render: (t) => t || '—' },
                                { title: 'Machine', dataIndex: 'machine_group_name', key: 'machine', render: (t) => t || '—' },
                              ]}
                              scroll={{ x: 600 }}
                            />
                          </div>
                        )}

                        {/* Products */}
                        {op.products?.length > 0 && (
                          <div>
                            <Tooltip title="Output products from this operation"><Tag color="cyan" style={{ fontSize: 12, marginBottom: 4 }}>Products ({op.products.length})</Tag></Tooltip>
                            <Table size="small" bordered pagination={false}
                              dataSource={op.products.map((p, i) => ({ ...p, key: i }))}
                              columns={[
                                { title: 'Product', dataIndex: 'product', key: 'product' },
                                { title: 'Theoretical', dataIndex: 'theoretical_value', key: 'theo', align: 'center', width: 90, render: (t) => t ?? '—' },
                                { title: 'Min', dataIndex: 'min_value', key: 'min', align: 'center', width: 60, render: (t) => t ?? '—' },
                                { title: 'Max', dataIndex: 'max_value', key: 'max', align: 'center', width: 60, render: (t) => t ?? '—' },
                                { title: 'Unit', dataIndex: 'unit', key: 'unit', align: 'center', width: 50, render: (t) => t || '—' },
                                { title: 'Instruction', dataIndex: 'instruction', key: 'inst', ellipsis: true, render: (t) => t || '—' },
                              ]}
                              scroll={{ x: 500 }}
                            />
                          </div>
                        )}

                        {/* Action List */}
                        {op.action_list?.length > 0 && (
                          <div>
                            <Tooltip title="Actions to be performed during this operation"><Tag color="orange" style={{ fontSize: 12, marginBottom: 4 }}>Action List ({op.action_list.length})</Tag></Tooltip>
                            <Table size="small" bordered pagination={false}
                              dataSource={op.action_list.map((a, i) => ({ ...a, key: i }))}
                              columns={[
                                { title: 'Action', dataIndex: 'name', key: 'name' },
                                { title: 'Machine', dataIndex: 'machine_group_name', key: 'machine', render: (t) => t || '—' },
                                { title: 'Fields', key: 'fields', render: (_, r) => r.fields?.map((f, fi) => <Tag key={fi} style={{ fontSize: 10 }}>{f.type}: {f.name}</Tag>) || '—' },
                              ]}
                            />
                          </div>
                        )}

                        {/* Checklist */}
                        {op.checklist?.length > 0 && (
                          <div>
                            <Tooltip title="Checklist items to verify before/during this operation"><Tag color="gold" style={{ fontSize: 12, marginBottom: 4 }}>Checklist ({op.checklist.length})</Tag></Tooltip>
                            <Table size="small" bordered pagination={false}
                              dataSource={op.checklist.map((c, i) => ({ ...c, key: i }))}
                              columns={[
                                { title: '#', key: 'idx', width: 40, align: 'center', render: (_, __, idx) => idx + 1 },
                                { title: 'Checklist Item', dataIndex: 'checklist_label', key: 'label' },
                                { title: 'Created By', dataIndex: 'created_by', key: 'by', width: 100, render: (t) => t || '—' },
                              ]}
                            />
                          </div>
                        )}

                        {/* Sampling */}
                        {op.sampling?.length > 0 && (
                          <div>
                            <Tooltip title="Samples to be collected and tested"><Tag color="lime" style={{ fontSize: 12, marginBottom: 4 }}>Sampling ({op.sampling.length})</Tag></Tooltip>
                            <Table size="small" bordered pagination={false}
                              dataSource={op.sampling.map((s, i) => ({ ...s, key: i }))}
                              columns={[
                                { title: 'Sample', dataIndex: 'sample', key: 'sample' },
                                { title: 'Qty', dataIndex: 'quantity', key: 'qty', align: 'center', width: 60, render: (t) => t ?? '—' },
                                { title: 'Unit', dataIndex: 'unit', key: 'unit', align: 'center', width: 50, render: (t) => t || '—' },
                                { title: 'Test Type', dataIndex: 'test_type', key: 'test', render: (t) => t || '—' },
                                { title: 'Parameter', dataIndex: 'parameter', key: 'param', render: (t) => t || '—' },
                                { title: 'Min', dataIndex: 'min_value', key: 'min', align: 'center', width: 55, render: (t) => t ?? '—' },
                                { title: 'Max', dataIndex: 'max_value', key: 'max', align: 'center', width: 55, render: (t) => t ?? '—' },
                                { title: 'Send To', dataIndex: 'to_name', key: 'to', render: (t) => t || '—' },
                              ]}
                              scroll={{ x: 600 }}
                            />
                          </div>
                        )}

                        {/* Line Clearance */}
                        {op.line_clearance?.length > 0 && (
                          <div>
                            <Tooltip title="Line clearance and cleaning verification"><Tag color="magenta" style={{ fontSize: 12, marginBottom: 4 }}>Line Clearance ({op.line_clearance.length})</Tag></Tooltip>
                            <Table size="small" bordered pagination={false}
                              dataSource={op.line_clearance.map((l, i) => ({ ...l, key: i }))}
                              columns={[
                                { title: 'Clearance Type', dataIndex: 'clearance_type', key: 'type' },
                                { title: 'Status', dataIndex: 'type', key: 'status', width: 70, align: 'center', render: (t) => <Tag color={t === 'Pass' ? 'green' : 'red'}>{t}</Tag> },
                                { title: 'Cleaning', key: 'cleaning', render: (_, r) => r.cleaning_type?.join(', ') || '—' },
                                { title: 'Instruction', dataIndex: 'instruction', key: 'inst', ellipsis: true, render: (t) => t || '—' },
                              ]}
                            />
                          </div>
                        )}

                        {/* API Calculation */}
                        {op.api_calculation?.length > 0 && (
                          <div>
                            <Tooltip title="API yield and calculation formulas"><Tag color="purple" style={{ fontSize: 12, marginBottom: 4 }}>API Calculation ({op.api_calculation.length})</Tag></Tooltip>
                            <Table size="small" bordered pagination={false}
                              dataSource={op.api_calculation.map((a, i) => ({ ...a, key: i }))}
                              columns={[
                                { title: 'Type', dataIndex: 'type', key: 'type' },
                                { title: 'Formula', dataIndex: 'formula', key: 'formula', ellipsis: true },
                                { title: 'Expected', dataIndex: 'expected_value', key: 'exp', align: 'center', width: 80 },
                                { title: 'Min', dataIndex: 'min_value', key: 'min', align: 'center', width: 55, render: (t) => t ?? '—' },
                                { title: 'Max', dataIndex: 'max_value', key: 'max', align: 'center', width: 55, render: (t) => t ?? '—' },
                                { title: 'Unit', dataIndex: 'unit', key: 'unit', align: 'center', width: 50, render: (t) => t || '—' },
                              ]}
                            />
                          </div>
                        )}

                        {/* Machines */}
                        {op.machines?.filter(m => m.machine_group?.length > 0)?.length > 0 && (
                          <div>
                            <Tooltip title="Machines and equipment used in this operation"><Tag color="volcano" style={{ fontSize: 12, marginBottom: 4 }}>Machines</Tag></Tooltip>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                              {op.machines.flatMap((m) => m.machine_group || []).map((name, mi) => (
                                <Tag key={mi}>{name}</Tag>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
